import React, {ReactNode, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {css} from "@emotion/react";
import type {LatLngExpression} from "leaflet";

import {Button} from "@pg-design/button-module";
import {Checkbox} from "@pg-design/checkbox-module";
import {borderRadius, calculateRemSize, mr, mt, pb, ph, pt, pv} from "@pg-design/helpers-css-strings";
import {Loader} from "@pg-design/loader-module";
import {getThemeBreakpoint, getThemeVariable} from "@pg-design/styles-strings";
import {AdvancedScroll} from "@pg-mono/advanced-scroll-module";
import {noop} from "@pg-mono/nodash";
import {convertToArrayOfLatLngLiterals, IPolyline, LazyOpenStreetMap} from "@pg-mono/open-street-map";
import {useUserDevice} from "@pg-mono/user-device";
import {RegionType} from "@pg-rp/common-types";

import {useInteractiveRegionsMapContext} from "../hooks/use_interactive_regions_map_context";
import {useMapRegionDistrictsAndSurroundings} from "../hooks/use_map_region_districts_and_surroundings";
import {useSearchInteractiveRegionSelection} from "../hooks/use_search_interactive_region_selection";
import {IInteractiveRegion} from "../types/IInteractiveRegion";
import {InteractiveRegionsMapLayer} from "./InteractiveRegionsMapLayer";

interface IProps {
    /**
     * Map bottom-left overlay slot.
     */
    mapBottomLeftSlot?: () => ReactNode;
    /**
     * Optional render prop for bottom controls. If provided and updateRegionsImmediately is false,
     * it will replace the default "submit" button. Receives helpers like handleSubmitRegions.
     */
    bottomControlsSlot?: ((args: {handleSubmitRegions: () => void}) => ReactNode) | false;
    /**
     * Height of the map in pixels. Defaults to 560px on desktop and 380px on mobile.
     */
    mapHeight?: number;
    /**
     * Height of the map on mobile in pixels. If not provided, defaults to 380px.
     */
    mapHeightMobile?: number;
    /**
     *  Optional element to show in case there is no map to show, if needed
     */
    noMapElement?: React.ReactNode;
}

export const InteractiveRegionsMap = (props: IProps) => {
    const {mapHeight = 560, mapHeightMobile = 300, bottomControlsSlot, mapBottomLeftSlot, noMapElement} = props;

    const [regionsToHover, setRegionsToHover] = useState<number[]>([]);
    const {isMobile} = useUserDevice();
    const controlSlotRef = useRef<HTMLDivElement | null>(null);

    const {availableDistrictsAndSurroundings, regions} = useMapRegionDistrictsAndSurroundings();
    const {isInModal, updateRegionsImmediately, showMainRegionMapOutline, tracking, selectedRegions, handleUpdateRegions} = useInteractiveRegionsMapContext();

    const {
        selectedRegionIdsState,
        selectedRegionIds,
        hasAllDistrictBeenSelected,
        handleSelectedRegionsChange,
        toggleSelectedDistrict,
        syncSelectedRegionsWithRemoteSelection
    } = useSearchInteractiveRegionSelection({
        regions,
        allAvailableRegions: availableDistrictsAndSurroundings || []
    });

    useEffect(() => {
        tracking?.onMapInit({
            regionName: regions?.main?.short_name || "na"
        });
    }, []);

    useEffect(() => {
        if (selectedRegions && regions.main?.id && availableDistrictsAndSurroundings) {
            syncSelectedRegionsWithRemoteSelection(selectedRegions);
        }
    }, [selectedRegions, regions.main?.id, availableDistrictsAndSurroundings]);

    useEffect(() => {
        if (updateRegionsImmediately) {
            handleSubmitRegions();
        }
    }, [selectedRegionIds]);

    const handleSubmitRegions = () => {
        if (!availableDistrictsAndSurroundings) {
            return;
        }

        let selectedRegions = availableDistrictsAndSurroundings?.filter((item) => selectedRegionIds.includes(item.id));

        if (hasAllDistrictBeenSelected && regions.main) {
            selectedRegions = [...selectedRegions.filter((item) => item.type !== RegionType.DISTRICT), regions.main];
        }

        !updateRegionsImmediately &&
            tracking?.onMapUpdate({
                regionName: regions?.main?.short_name || "na"
            });

        handleUpdateRegions(
            selectedRegions,
            (regions.main && {
                id: regions.main.id,
                name: regions.main.short_name,
                slug: regions.main.slug
            }) ||
                null
        );
    };

    const onRegionClick = useCallback(
        (regionId: number) => {
            handleSelectedRegionsChange(regionId);
        },
        [handleSelectedRegionsChange]
    );

    const handleCheckboxMouseEnter = (region: IInteractiveRegion) => {
        if (!isMobile) {
            setRegionsToHover([region.id]);
        }
    };

    const handleCheckboxMouseLeave = (region: IInteractiveRegion) => {
        if (!isMobile) {
            setRegionsToHover((prev) =>
                prev.filter((id) => {
                    return id !== region.id;
                })
            );
        }
    };

    const renderDistrictCheckboxes = () => {
        return (
            <>
                {regions.districts.length > 0 && (
                    <>
                        <Checkbox
                            checkedMarkColor="#02D054"
                            id="all_districts_checkbox"
                            name="all_districts_checkbox"
                            labelContent="Wszystkie dzielnice"
                            checked={hasAllDistrictBeenSelected}
                            onChange={(_, checked) => {
                                tracking?.onRegionCheckboxToggle({
                                    regionName: regions?.main?.short_name || "na",
                                    selectionName: "Wszystkie dzielnice",
                                    action: checked ? "on" : "off"
                                });

                                toggleSelectedDistrict();
                            }}
                            value="all"
                        />
                        {regions.districts.map((item) => {
                            const checked = selectedRegionIds.includes(item.id);
                            const key = `region_${item.id}`;

                            return (
                                <Checkbox
                                    key={key}
                                    id={key}
                                    checkedMarkColor="#02D054"
                                    name={item.name_declension_what}
                                    labelContent={item.short_name.split(", ").pop()}
                                    checked={checked || hasAllDistrictBeenSelected}
                                    onChange={(_, checked) => {
                                        tracking?.onRegionCheckboxToggle({
                                            regionName: regions?.main?.short_name || "na",
                                            selectionName: item.short_name,
                                            action: checked ? "on" : "off"
                                        });

                                        handleSelectedRegionsChange(item.id);
                                    }}
                                    onMouseEnter={() => {
                                        handleCheckboxMouseEnter(item);
                                    }}
                                    onMouseLeave={() => {
                                        handleCheckboxMouseLeave(item);
                                    }}
                                    value={item.id}
                                    css={checkboxWrapperStyle}
                                />
                            );
                        })}
                    </>
                )}

                {regions.surroundings.length > 0 && (
                    <div css={[surroundingsWrapperStyle, regions.districts.length > 0 && surroundingsDistrictSeparatorStyle]}>
                        {regions.surroundings.map((item) => {
                            const checked = selectedRegionIds.includes(item.id);
                            const key = `region_${item.id}`;

                            return (
                                <Checkbox
                                    key={key}
                                    id={key}
                                    checkedMarkColor="#02D054"
                                    name={item.name_declension_what}
                                    labelContent={item.short_name.split(", ").pop()}
                                    checked={checked}
                                    onChange={() => {
                                        handleSelectedRegionsChange(item.id);
                                    }}
                                    onMouseEnter={() => {
                                        handleCheckboxMouseEnter(item);
                                    }}
                                    onMouseLeave={() => {
                                        handleCheckboxMouseLeave(item);
                                    }}
                                    value={item.id}
                                    css={checkboxWrapperStyle}
                                />
                            );
                        })}
                    </div>
                )}
            </>
        );
    };

    const polylines: IPolyline[] | undefined = useMemo(() => {
        return showMainRegionMapOutline && regions.main && regions.main?.area?.geo_area?.coordinates
            ? [
                  {
                      id: regions.main.id.toString(),
                      positions: convertToArrayOfLatLngLiterals(regions.main?.area?.geo_area?.coordinates, {reversedValues: true}).flat(
                          Infinity
                      ) as LatLngExpression[],
                      pane: "topPane",
                      pathOptions: {
                          color: "rgba(0, 0, 0, 0.3)",
                          weight: 1
                      }
                  }
              ]
            : undefined;
    }, [regions.main?.id, showMainRegionMapOutline]);

    return (
        <div css={wrapperStyle}>
            <div css={[regionsWrapperStyle, mapOffsetStyle((isMobile && controlSlotRef.current?.clientHeight) || "unset")]}>
                <div css={[mapColumnStyle, isInModal && modalDesktopColumnStyle]}>
                    <React.Suspense
                        fallback={
                            <div css={loaderWrapperStyle(mapHeight, mapHeightMobile)}>
                                <Loader size="lg" />
                            </div>
                        }
                    >
                        {availableDistrictsAndSurroundings ? (
                            <LazyOpenStreetMap polylines={polylines} css={mapStyle(mapHeight, mapHeightMobile)} fitBounds>
                                <InteractiveRegionsMapLayer
                                    regionsToHover={regionsToHover}
                                    staticRegions={regions.main ? [regions.main] : []}
                                    regions={availableDistrictsAndSurroundings}
                                    hasAllDistrictBeenSelected={hasAllDistrictBeenSelected}
                                    selectedFeaturesState={[selectedRegionIdsState[0], noop]}
                                    onRegionClick={onRegionClick}
                                />
                            </LazyOpenStreetMap>
                        ) : (
                            noMapElement || null
                        )}
                    </React.Suspense>

                    {mapBottomLeftSlot ? <div css={bottomLeftSlotWrapperStyle}>{mapBottomLeftSlot()}</div> : null}
                </div>

                {availableDistrictsAndSurroundings ? (
                    <div css={[regionListColumnStyle, isInModal && modalDesktopColumnStyle]}>
                        {isInModal ? (
                            renderDistrictCheckboxes()
                        ) : (
                            <AdvancedScroll autoHeight css={regionListScrollWrapperStyle}>
                                {renderDistrictCheckboxes()}
                            </AdvancedScroll>
                        )}
                    </div>
                ) : null}
            </div>

            {!updateRegionsImmediately && bottomControlsSlot !== false && (
                <div ref={controlSlotRef} css={buttonWrapperStyle}>
                    {bottomControlsSlot ? (
                        bottomControlsSlot({handleSubmitRegions})
                    ) : (
                        <Button type="button" size="small" variant="filled_primary" onClick={handleSubmitRegions} css={buttonStyle}>
                            Zatwierdź
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

const wrapperStyle = css`
    display: flex;
    flex-direction: column;
`;

const regionsWrapperStyle = css`
    display: flex;
    flex-direction: column;
    background-color: white;

    @media (min-width: ${getThemeBreakpoint().md}) {
        flex-direction: row;
        gap: ${calculateRemSize(2)};
    }
`;

const buttonWrapperStyle = css`
    ${pb(3)};
    ${ph(3)};
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 1000;

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${pt(3)};
        ${pb(0)};
        ${ph(0)};
        position: sticky;
        bottom: 0;
        background-color: white;
    }
`;

const mapOffsetStyle = (offset: number | string) => css`
    padding-bottom: ${typeof offset === "string" ? offset : `${offset}px`};
`;

const buttonStyle = () => css`
    width: 100%;
    box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.19);

    @media (min-width: ${getThemeBreakpoint().md}) {
        box-shadow: none;
    }
`;

const bottomLeftSlotWrapperStyle = css`
    position: absolute;
    bottom: ${calculateRemSize(1)};
    left: ${calculateRemSize(1)};
    background: #fff;
    border: 1px solid ${getThemeVariable("colors.gray.600")};
    z-index: 1000; // should be higher than map
    ${borderRadius(1)};
`;

const mapColumnStyle = css`
    flex: 1 1 100%;
    position: relative;
`;

const mapStyle = (mapHeight: number, mapHeightMobile: number) => css`
    width: 100%;
    height: ${mapHeightMobile}px;
    position: initial;

    @media (min-width: ${getThemeBreakpoint().md}) {
        height: ${mapHeight}px;
    }
`;

const loaderWrapperStyle = (mapHeight: number, mapHeightMobile: number) => css`
    height: ${mapHeightMobile}px;
    display: flex;
    justify-content: center;
    align-items: center;

    @media (min-width: ${getThemeBreakpoint().md}) {
        height: ${mapHeight}px;
    }
`;

const regionListColumnStyle = css`
    ${pv(4)};
    flex: 1 1 100%;
    display: flex;
    flex-direction: column;
    gap: ${calculateRemSize(2)};

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${pv(0)};
        flex: 0 0 24rem;
        gap: ${calculateRemSize(1)};
    }
`;

const modalDesktopColumnStyle = css`
    @media (min-width: ${getThemeBreakpoint().md}) {
        max-height: 56rem;
        overflow: auto;
    }
`;

const surroundingsWrapperStyle = css`
    ${pb(1)};
    ${mr(2)};
    text-align: left;
`;

const surroundingsDistrictSeparatorStyle = css`
    ${pt(1)};
    ${mt(1)};
    border-top: 1px solid ${getThemeVariable("colors.gray.400")};
`;

const checkboxWrapperStyle = css`
    align-items: flex-start;
    width: 100%;
    line-height: ${calculateRemSize(3)};
    text-align: left;
`;

const regionListScrollWrapperStyle = css`
    display: flex;
    flex-direction: column;
    gap: ${calculateRemSize(2)};
    text-align: left;

    @media (min-width: ${getThemeBreakpoint().md}) {
        flex: 0 0 auto;
        gap: ${calculateRemSize(1)};
    }
`;
import React, {ReactNode, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {css} from "@emotion/react";
import type {LatLngExpression} from "leaflet";

import {Button} from "@pg-design/button-module";
import {Checkbox} from "@pg-design/checkbox-module";
import {borderRadius, calculateRemSize, mr, mt, pb, ph, pt, pv} from "@pg-design/helpers-css-strings";
import {Loader} from "@pg-design/loader-module";
import {getThemeBreakpoint, getThemeVariable} from "@pg-design/styles-strings";
import {AdvancedScroll} from "@pg-mono/advanced-scroll-module";
import {noop} from "@pg-mono/nodash";
import {convertToArrayOfLatLngLiterals, IPolyline, LazyOpenStreetMap} from "@pg-mono/open-street-map";
import {useUserDevice} from "@pg-mono/user-device";
import {RegionType} from "@pg-rp/common-types";

import {useInteractiveRegionsMapContext} from "../hooks/use_interactive_regions_map_context";
import {useMapRegionDistrictsAndSurroundings} from "../hooks/use_map_region_districts_and_surroundings";
import {useSearchInteractiveRegionSelection} from "../hooks/use_search_interactive_region_selection";
import {IInteractiveRegion} from "../types/IInteractiveRegion";
import {InteractiveRegionsMapLayer} from "./InteractiveRegionsMapLayer";

interface IProps {
    /**
     * Map bottom-left overlay slot.
     */
    mapBottomLeftSlot?: () => ReactNode;
    /**
     * Optional render prop for bottom controls. If provided and updateRegionsImmediately is false,
     * it will replace the default "submit" button. Receives helpers like handleSubmitRegions.
     */
    bottomControlsSlot?: ((args: {handleSubmitRegions: () => void}) => ReactNode) | false;
    /**
     * Height of the map in pixels. Defaults to 560px on desktop and 380px on mobile.
     */
    mapHeight?: number;
    /**
     * Height of the map on mobile in pixels. If not provided, defaults to 380px.
     */
    mapHeightMobile?: number;
    /**
     *  Optional element to show in case there is no map to show, if needed
     */
    noMapElement?: React.ReactNode;
}

export const InteractiveRegionsMap = (props: IProps) => {
    const {mapHeight = 560, mapHeightMobile = 300, bottomControlsSlot, mapBottomLeftSlot, noMapElement} = props;

    const [regionsToHover, setRegionsToHover] = useState<number[]>([]);
    const {isMobile} = useUserDevice();
    const controlSlotRef = useRef<HTMLDivElement | null>(null);

    const {availableDistrictsAndSurroundings, regions} = useMapRegionDistrictsAndSurroundings();
    const {isInModal, updateRegionsImmediately, showMainRegionMapOutline, tracking, selectedRegions, handleUpdateRegions} = useInteractiveRegionsMapContext();

    const {
        selectedRegionIdsState,
        selectedRegionIds,
        hasAllDistrictBeenSelected,
        handleSelectedRegionsChange,
        toggleSelectedDistrict,
        syncSelectedRegionsWithRemoteSelection
    } = useSearchInteractiveRegionSelection({
        regions,
        allAvailableRegions: availableDistrictsAndSurroundings || []
    });

    useEffect(() => {
        tracking?.onMapInit({
            regionName: regions?.main?.short_name || "na"
        });
    }, []);

    useEffect(() => {
        if (selectedRegions && regions.main?.id && availableDistrictsAndSurroundings) {
            syncSelectedRegionsWithRemoteSelection(selectedRegions);
        }
    }, [selectedRegions, regions.main?.id, availableDistrictsAndSurroundings]);

    useEffect(() => {
        if (updateRegionsImmediately) {
            handleSubmitRegions();
        }
    }, [selectedRegionIds]);

    const handleSubmitRegions = () => {
        if (!availableDistrictsAndSurroundings) {
            return;
        }

        let selectedRegions = availableDistrictsAndSurroundings?.filter((item) => selectedRegionIds.includes(item.id));

        if (hasAllDistrictBeenSelected && regions.main) {
            selectedRegions = [...selectedRegions.filter((item) => item.type !== RegionType.DISTRICT), regions.main];
        }

        !updateRegionsImmediately &&
            tracking?.onMapUpdate({
                regionName: regions?.main?.short_name || "na"
            });

        handleUpdateRegions(
            selectedRegions,
            (regions.main && {
                id: regions.main.id,
                name: regions.main.short_name,
                slug: regions.main.slug
            }) ||
                null
        );
    };

    const onRegionClick = useCallback(
        (regionId: number) => {
            handleSelectedRegionsChange(regionId);
        },
        [handleSelectedRegionsChange]
    );

    const handleCheckboxMouseEnter = (region: IInteractiveRegion) => {
        if (!isMobile) {
            setRegionsToHover([region.id]);
        }
    };

    const handleCheckboxMouseLeave = (region: IInteractiveRegion) => {
        if (!isMobile) {
            setRegionsToHover((prev) =>
                prev.filter((id) => {
                    return id !== region.id;
                })
            );
        }
    };

    const renderDistrictCheckboxes = () => {
        return (
            <>
                {regions.districts.length > 0 && (
                    <>
                        <Checkbox
                            checkedMarkColor="#02D054"
                            id="all_districts_checkbox"
                            name="all_districts_checkbox"
                            labelContent="Wszystkie dzielnice"
                            checked={hasAllDistrictBeenSelected}
                            onChange={(_, checked) => {
                                tracking?.onRegionCheckboxToggle({
                                    regionName: regions?.main?.short_name || "na",
                                    selectionName: "Wszystkie dzielnice",
                                    action: checked ? "on" : "off"
                                });

                                toggleSelectedDistrict();
                            }}
                            value="all"
                        />
                        {regions.districts.map((item) => {
                            const checked = selectedRegionIds.includes(item.id);
                            const key = `region_${item.id}`;

                            return (
                                <Checkbox
                                    key={key}
                                    id={key}
                                    checkedMarkColor="#02D054"
                                    name={item.name_declension_what}
                                    labelContent={item.short_name.split(", ").pop()}
                                    checked={checked || hasAllDistrictBeenSelected}
                                    onChange={(_, checked) => {
                                        tracking?.onRegionCheckboxToggle({
                                            regionName: regions?.main?.short_name || "na",
                                            selectionName: item.short_name,
                                            action: checked ? "on" : "off"
                                        });

                                        handleSelectedRegionsChange(item.id);
                                    }}
                                    onMouseEnter={() => {
                                        handleCheckboxMouseEnter(item);
                                    }}
                                    onMouseLeave={() => {
                                        handleCheckboxMouseLeave(item);
                                    }}
                                    value={item.id}
                                    css={checkboxWrapperStyle}
                                />
                            );
                        })}
                    </>
                )}

                {regions.surroundings.length > 0 && (
                    <div css={[surroundingsWrapperStyle, regions.districts.length > 0 && surroundingsDistrictSeparatorStyle]}>
                        {regions.surroundings.map((item) => {
                            const checked = selectedRegionIds.includes(item.id);
                            const key = `region_${item.id}`;

                            return (
                                <Checkbox
                                    key={key}
                                    id={key}
                                    checkedMarkColor="#02D054"
                                    name={item.name_declension_what}
                                    labelContent={item.short_name.split(", ").pop()}
                                    checked={checked}
                                    onChange={() => {
                                        handleSelectedRegionsChange(item.id);
                                    }}
                                    onMouseEnter={() => {
                                        handleCheckboxMouseEnter(item);
                                    }}
                                    onMouseLeave={() => {
                                        handleCheckboxMouseLeave(item);
                                    }}
                                    value={item.id}
                                    css={checkboxWrapperStyle}
                                />
                            );
                        })}
                    </div>
                )}
            </>
        );
    };

    const polylines: IPolyline[] | undefined = useMemo(() => {
        return showMainRegionMapOutline && regions.main && regions.main?.area?.geo_area?.coordinates
            ? [
                  {
                      id: regions.main.id.toString(),
                      positions: convertToArrayOfLatLngLiterals(regions.main?.area?.geo_area?.coordinates, {reversedValues: true}).flat(
                          Infinity
                      ) as LatLngExpression[],
                      pane: "topPane",
                      pathOptions: {
                          color: "rgba(0, 0, 0, 0.3)",
                          weight: 1
                      }
                  }
              ]
            : undefined;
    }, [regions.main?.id, showMainRegionMapOutline]);

    return (
        <div css={wrapperStyle}>
            <div css={[regionsWrapperStyle, mapOffsetStyle((isMobile && controlSlotRef.current?.clientHeight) || "unset")]}>
                <div css={[mapColumnStyle, isInModal && modalDesktopColumnStyle]}>
                    <React.Suspense
                        fallback={
                            <div css={loaderWrapperStyle(mapHeight, mapHeightMobile)}>
                                <Loader size="lg" />
                            </div>
                        }
                    >
                        {availableDistrictsAndSurroundings ? (
                            <LazyOpenStreetMap polylines={polylines} css={mapStyle(mapHeight, mapHeightMobile)} fitBounds>
                                <InteractiveRegionsMapLayer
                                    regionsToHover={regionsToHover}
                                    staticRegions={regions.main ? [regions.main] : []}
                                    regions={availableDistrictsAndSurroundings}
                                    hasAllDistrictBeenSelected={hasAllDistrictBeenSelected}
                                    selectedFeaturesState={[selectedRegionIdsState[0], noop]}
                                    onRegionClick={onRegionClick}
                                />
                            </LazyOpenStreetMap>
                        ) : (
                            noMapElement || null
                        )}
                    </React.Suspense>

                    {mapBottomLeftSlot ? <div css={bottomLeftSlotWrapperStyle}>{mapBottomLeftSlot()}</div> : null}
                </div>

                {availableDistrictsAndSurroundings ? (
                    <div css={[regionListColumnStyle, isInModal && modalDesktopColumnStyle]}>
                        {isInModal ? (
                            renderDistrictCheckboxes()
                        ) : (
                            <AdvancedScroll autoHeight css={regionListScrollWrapperStyle}>
                                {renderDistrictCheckboxes()}
                            </AdvancedScroll>
                        )}
                    </div>
                ) : null}
            </div>

            {!updateRegionsImmediately && bottomControlsSlot !== false && (
                <div ref={controlSlotRef} css={buttonWrapperStyle}>
                    {bottomControlsSlot ? (
                        bottomControlsSlot({handleSubmitRegions})
                    ) : (
                        <Button type="button" size="small" variant="filled_primary" onClick={handleSubmitRegions} css={buttonStyle}>
                            Zatwierdź
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

const wrapperStyle = css`
    display: flex;
    flex-direction: column;
`;

const regionsWrapperStyle = css`
    display: flex;
    flex-direction: column;
    background-color: white;

    @media (min-width: ${getThemeBreakpoint().md}) {
        flex-direction: row;
        gap: ${calculateRemSize(2)};
    }
`;

const buttonWrapperStyle = css`
    ${pb(3)};
    ${ph(3)};
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 1000;

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${pt(3)};
        ${pb(0)};
        ${ph(0)};
        position: sticky;
        bottom: 0;
        background-color: white;
    }
`;

const mapOffsetStyle = (offset: number | string) => css`
    padding-bottom: ${typeof offset === "string" ? offset : `${offset}px`};
`;

const buttonStyle = () => css`
    width: 100%;
    box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.19);

    @media (min-width: ${getThemeBreakpoint().md}) {
        box-shadow: none;
    }
`;

const bottomLeftSlotWrapperStyle = css`
    position: absolute;
    bottom: ${calculateRemSize(1)};
    left: ${calculateRemSize(1)};
    background: #fff;
    border: 1px solid ${getThemeVariable("colors.gray.600")};
    z-index: 1000; // should be higher than map
    ${borderRadius(1)};
`;

const mapColumnStyle = css`
    flex: 1 1 100%;
    position: relative;
`;

const mapStyle = (mapHeight: number, mapHeightMobile: number) => css`
    width: 100%;
    height: ${mapHeightMobile}px;
    position: initial;

    @media (min-width: ${getThemeBreakpoint().md}) {
        height: ${mapHeight}px;
    }
`;

const loaderWrapperStyle = (mapHeight: number, mapHeightMobile: number) => css`
    height: ${mapHeightMobile}px;
    display: flex;
    justify-content: center;
    align-items: center;

    @media (min-width: ${getThemeBreakpoint().md}) {
        height: ${mapHeight}px;
    }
`;

const regionListColumnStyle = css`
    ${pv(4)};
    flex: 1 1 100%;
    display: flex;
    flex-direction: column;
    gap: ${calculateRemSize(2)};

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${pv(0)};
        flex: 0 0 24rem;
        gap: ${calculateRemSize(1)};
    }
`;

const modalDesktopColumnStyle = css`
    @media (min-width: ${getThemeBreakpoint().md}) {
        max-height: 56rem;
        overflow: auto;
    }
`;

const surroundingsWrapperStyle = css`
    ${pb(1)};
    ${mr(2)};
    text-align: left;
`;

const surroundingsDistrictSeparatorStyle = css`
    ${pt(1)};
    ${mt(1)};
    border-top: 1px solid ${getThemeVariable("colors.gray.400")};
`;

const checkboxWrapperStyle = css`
    align-items: flex-start;
    width: 100%;
    line-height: ${calculateRemSize(3)};
    text-align: left;
`;

const regionListScrollWrapperStyle = css`
    display: flex;
    flex-direction: column;
    gap: ${calculateRemSize(2)};
    text-align: left;

    @media (min-width: ${getThemeBreakpoint().md}) {
        flex: 0 0 auto;
        gap: ${calculateRemSize(1)};
    }
`;
